"use client";

import { Paths, pathToEditCatalog } from "@app/routes";
import {
  ConfirmAction,
  ErrorFallback,
  ScrollSyncTabs,
  WithPermission,
} from "@atlas/composite";
import { Button, useToast } from "@atlas/core";
import { usePermission, useSessionData, useTranslation } from "@atlas/sdk";
import {
  CatalogMode,
  HubPackageStatus,
  IHubCatalogStatus,
  IHubCatalogVisibility,
  type IHubPackageVisibility,
  IHubReviewStatus,
} from "@enums";
import type { IHubCatalog, IHubPackage } from "@models";
import {
  useCreateHubCart,
  useCreateHubPackage,
  useCreateHubReview,
  useHubCatalogByVersion,
  useHubCatalogType,
  useHubCurrentCart,
  useHubPackage,
  useUpdateHubCatalog,
  useUpdateHubPackage,
  useUserInfo,
} from "@query";
import {
  BodyFooterLayout,
  defaultHubCartPackageId,
  HUB_PERM,
  parseHubManagerTabParam,
  useNavigateBack,
} from "@shared";
import {
  CatalogVisibilityUpgradeAction,
  getCatalogVisibilityUpgradeAction,
  mergePackageWithCatalogRelation,
  removePackageCatalogRelation,
} from "@utils";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { type MouseEvent, useCallback, useMemo, useState } from "react";
import { createSearchParams, Link, useSearchParams } from "react-router";
import { CatalogTemplateStep } from "../catalog/CatalogTemplateStep";
import { CatalogAddons } from "./CatalogAddons";
import { CatalogComments } from "./CatalogComments";
import { CatalogDescription } from "./CatalogDescription";
import { CatalogHeader } from "./CatalogHeader";
import { CatalogMetadata } from "./CatalogMetadata";
import { CatalogOverview } from "./CatalogOverview";
import { MediaGallery } from "./MediaGallery";

function parseOptionalPositiveIntParam(raw: string | null): number | undefined {
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function showModeratorReviewRejectStatus(status: IHubCatalogStatus): boolean {
  return (
    status === IHubCatalogStatus.Review || status === IHubCatalogStatus.Rejected
  );
}

function showModeratorReviewApproveStatus(status: IHubCatalogStatus): boolean {
  return (
    status === IHubCatalogStatus.Review || status === IHubCatalogStatus.Rejected
  );
}

type PendingModeratorAction = { approved: boolean };

interface ICatalogInfoWrapperProps {
  catalogId?: number;
  mode?: CatalogMode;
  previewCatalog?: IHubCatalog | null;
  version?: string;
}

export function CatalogInfoWrapper({
  catalogId: catalogIdProp,
  mode = CatalogMode.Create,
  previewCatalog: previewCatalogProp = null,
  version: versionProp,
}: ICatalogInfoWrapperProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pendingModeratorAction, setPendingModeratorAction] =
    useState<PendingModeratorAction | null>(null);
  const [searchParams] = useSearchParams();
  const hubManagerReturnTab = parseHubManagerTabParam(searchParams.get("tab"));
  const catalogDetailsTabRule = useMemo((): string[] => {
    if (mode !== CatalogMode.Details) return [];
    if (
      hubManagerReturnTab == null ||
      hubManagerReturnTab === "explorer" ||
      hubManagerReturnTab === "subscriptions"
    ) {
      return [];
    }
    if (hubManagerReturnTab === "myCatalogs") {
      return [HUB_PERM.CATALOG_CREATE];
    }
    return [HUB_PERM.CATALOG_REVIEW_VIEW, HUB_PERM.CATALOG_REVIEW_PARTICIPATE];
  }, [mode, hubManagerReturnTab]);
  const catalogIdFromQuery =
    parseOptionalPositiveIntParam(searchParams.get("catalogId")) ?? NaN;
  const catalogId =
    catalogIdProp !== undefined ? catalogIdProp : catalogIdFromQuery;
  const versionFromQuery = searchParams.get("version");
  const effectiveVersion = versionProp ?? versionFromQuery;

  const navigateBack = useNavigateBack();

  const catalogManagerFallbackPath = useMemo(() => {
    const pathname = `/${Paths.CatalogManager}`;
    if (hubManagerReturnTab == null) return pathname;
    return `${pathname}?${createSearchParams({ tab: hubManagerReturnTab }).toString()}`;
  }, [hubManagerReturnTab]);

  const goBackOrToCatalogManager = useCallback(() => {
    navigateBack(catalogManagerFallbackPath);
  }, [navigateBack, catalogManagerFallbackPath]);

  const isFromExplorerHubTab = searchParams.get("tab") === "explorer";
  const isFromSubscriptionsHubTab = searchParams.get("tab") === "subscriptions";
  const isFromDeploymentsHubTab = searchParams.get("fromTab") === "deployments";
  const hasServerId = Number.isFinite(catalogId) && catalogId > 0;
  const idReady = hasServerId || previewCatalogProp != null;

  const { isPermission: hasCatalogBuy } = usePermission([HUB_PERM.CATALOG_BUY]);
  const cartPackageQueryEnabled =
    hasCatalogBuy && mode === CatalogMode.Details && isFromExplorerHubTab;
  const { userId } = useSessionData();
  const { data: userInfo } = useUserInfo();
  const companyId = userInfo?.companyId ?? 0;
  const uid = (userId ?? "").trim();
  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0 && cartPackageQueryEnabled,
  });
  const hubCartPackageId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );
  const { data: cartPackage } = useHubPackage(hubCartPackageId, {
    enabled: cartPackageQueryEnabled && hubCartPackageId > 0,
  });
  const { mutateAsync: createHubPackage, isPending: isCreatingHubPackage } =
    useCreateHubPackage();
  const { mutateAsync: updateHubPackage, isPending: isUpdatingHubPackage } =
    useUpdateHubPackage();
  const { mutateAsync: createHubCart } = useCreateHubCart();
  const { mutateAsync: updateHubCatalog } = useUpdateHubCatalog();
  const isPackageBusy = isCreatingHubPackage || isUpdatingHubPackage;

  const [upgradeConfirmState, setUpgradeConfirmState] = useState<{
    action: CatalogVisibilityUpgradeAction;
  } | null>(null);

  const { mutate: postPrbReview, isPending: isPostingPrbReview } =
    useCreateHubReview();

  const { data: fetchedCatalog, isLoading } = useHubCatalogByVersion(
    catalogId,
    effectiveVersion ?? "",
    {
      enabled: hasServerId && Boolean(effectiveVersion),
    },
  );
  const catalog = useMemo((): IHubCatalog | null => {
    return hasServerId
      ? (fetchedCatalog ?? null)
      : (previewCatalogProp ?? null);
  }, [hasServerId, fetchedCatalog, previewCatalogProp]);

  const catalogTypeDisplayLabel = (catalog?.catalogTypeLabel ?? "").trim();

  const { data: catalogType } = useHubCatalogType(catalog?.typeId ?? 0, {
    enabled: catalog?.typeId != null && catalog.typeId > 0,
  });

  const submitReviewDecision = useCallback(
    (approved: boolean) => {
      postPrbReview(
        {
          catalogId: catalog?.catalogId ?? 0,
          version: catalog?.version ?? 0,
          status: approved
            ? IHubReviewStatus.Approved
            : IHubReviewStatus.Rejected,
          reviewComments: approved
            ? "The catalog has been approved."
            : "The catalog has been rejected.",
        },
        {
          onSuccess: () => {
            toast.add({
              title: approved
                ? t("hub.catalog_manager.reviews_approve_success", {
                    defaultValue: "Review approved",
                  })
                : t("hub.catalog_manager.reviews_reject_success", {
                    defaultValue: "Review rejected",
                  }),
              data: { type: "success" },
            });
            goBackOrToCatalogManager();
          },
          onError: () => {
            toast.add({
              title: t("hub.catalog_manager.reviews_decision_error", {
                defaultValue: "Failed to update review",
              }),
              data: { type: "error" },
            });
          },
        },
      );
    },
    [catalog, postPrbReview, goBackOrToCatalogManager, toast, t],
  );

  const handleModeratorConfirm = useCallback(() => {
    const action = pendingModeratorAction;
    if (action == null) return;
    setPendingModeratorAction(null);
    submitReviewDecision(action.approved);
  }, [pendingModeratorAction, submitReviewDecision]);

  const moderatorConfirmDialog = useMemo(() => {
    if (pendingModeratorAction == null) return null;
    const name =
      catalog?.label?.trim() ||
      catalog?.name?.trim() ||
      t("hub.catalog_manager.this_catalog", { defaultValue: "this catalog" });
    if (pendingModeratorAction.approved) {
      return {
        title: t("hub.catalog_manager.review_confirm_approve_title", {
          defaultValue: "Approve review?",
        }),
        description: t(
          "hub.catalog_manager.review_confirm_approve_description",
          {
            name,
            defaultValue:
              'Approve the review for "{{name}}"? The catalog moves forward in the approval workflow.',
          },
        ),
        confirmText: t("hub.catalog_manager.reviews_approve", {
          defaultValue: "Approve",
        }),
        variant: "default" as const,
      };
    }
    return {
      title: t("hub.catalog_manager.review_confirm_reject_title", {
        defaultValue: "Reject review?",
      }),
      description: t("hub.catalog_manager.review_confirm_reject_description", {
        name,
        defaultValue:
          'Reject the review for "{{name}}"? The contributor will be notified.',
      }),
      confirmText: t("hub.catalog_manager.reviews_reject", {
        defaultValue: "Reject",
      }),
      variant: "destructive" as const,
    };
  }, [pendingModeratorAction, catalog, t]);

  const explorerCatalogInCart = useMemo(() => {
    const cid = catalog?.catalogId ?? 0;
    const version = catalog?.version ?? 0;
    if (cid <= 0 || cartPackage == null) return false;
    return (cartPackage.catalogRelations ?? []).some(
      (r: { catalogId?: number; catalogVersion?: number }) =>
        r.catalogId === cid && r.catalogVersion === version,
    );
  }, [cartPackage, catalog?.catalogId, catalog?.version]);

  const handleExplorerAddToCartClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const cid = catalog?.catalogId ?? 0;
      if (cid <= 0) {
        toast.add({
          title: t("hub.catalog_manager.package_invalid_catalog", {
            defaultValue: "Invalid catalog",
          }),
          data: { type: "error" },
        });
        return;
      }

      if (cartPackage == null) {
        const truncatedName = (catalog?.label || catalog?.name || "").substring(
          0,
          30,
        );
        const timestamp = dayjs().format("MM-DD-YYYY-HH-mm-ss");
        const generatedLabel = `Pkg-${truncatedName}-${timestamp}`;
        await handleExplorerNewPackageCreate({
          label: generatedLabel,
          description: `Auto-generated package for ${catalog?.label || catalog?.name}`,
          visibility:
            (catalog?.visibility as unknown as IHubPackageVisibility) ||
            "Private",
        });
        return;
      }

      const upgradeAction = getCatalogVisibilityUpgradeAction(
        catalog?.visibility,
        cartPackage.visibility,
      );

      if (upgradeAction !== CatalogVisibilityUpgradeAction.Allowed) {
        setUpgradeConfirmState({
          action: upgradeAction,
        });
        return;
      }

      if (cartPackage.packageId == null || cartPackage.packageId <= 0) {
        toast.add({
          title: t("hub.catalog_manager.package_update_missing_id", {
            defaultValue: "Could not update package",
          }),
          data: { type: "error" },
        });
        return;
      }

      const alreadyLinked = (cartPackage.catalogRelations ?? []).some(
        (r: { catalogId?: number }) => r.catalogId === cid,
      );
      if (alreadyLinked) {
        toast.add({
          title: t("hub.catalog_manager.package_catalog_already_linked", {
            defaultValue: "This catalog is already linked to your package.",
          }),
          data: { type: "default" },
        });
        return;
      }
      if (catalog == null) {
        throw new Error("missing catalog");
      }

      const body: IHubPackage = mergePackageWithCatalogRelation(
        cartPackage,
        catalog,
      );

      try {
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_update_success", {
            defaultValue: "Package updated",
          }),
          data: { type: "success" },
        });
      } catch {
        toast.add({
          title: t("hub.catalog_manager.package_update_error", {
            defaultValue: "Could not update package",
          }),
          data: { type: "error" },
        });
      }
    },
    [catalog, explorerCatalogInCart, cartPackage, t, toast, updateHubPackage],
  );

  const handleExplorerRemoveFromCartClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      const cid = catalog?.catalogId ?? 0;
      const version = catalog?.version ?? 0;
      if (cid <= 0 || cartPackage == null) return;

      const body: IHubPackage = removePackageCatalogRelation(
        cartPackage,
        cid,
        version,
      );

      try {
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_remove_success", {
            defaultValue: "Removed from package",
          }),
          data: { type: "success" },
        });
      } catch {
        toast.add({
          title: t("hub.catalog_manager.package_remove_error", {
            defaultValue: "Could not remove from package",
          }),
          data: { type: "error" },
        });
      }
    },
    [catalog, cartPackage, t, toast, updateHubPackage],
  );

  const handleUpgradeConfirm = useCallback(async () => {
    if (!upgradeConfirmState || !cartPackage || !catalog) return;
    const { action } = upgradeConfirmState;
    setUpgradeConfirmState(null);

    const newVisibility =
      action === CatalogVisibilityUpgradeAction.UpgradeCompany
        ? IHubCatalogVisibility.Company
        : IHubCatalogVisibility.Platform;

    const newStatus =
      action === CatalogVisibilityUpgradeAction.UpgradePlatformReview
        ? IHubCatalogStatus.Review
        : catalog.status;

    try {
      const updatedCatalog = await updateHubCatalog({
        ...catalog,
        visibility: newVisibility,
        status: newStatus,
      });

      if (action === CatalogVisibilityUpgradeAction.UpgradePlatformReview) {
        toast.add({
          title: t("hub.catalog_manager.catalog_review_submitted", {
            defaultValue: "Catalog submitted for review",
          }),
          data: { type: "success" },
        });
      } else {
        const body = mergePackageWithCatalogRelation(
          cartPackage,
          updatedCatalog,
        );
        await updateHubPackage(body);
        toast.add({
          title: t("hub.catalog_manager.package_update_success", {
            defaultValue: "Package updated",
          }),
          data: { type: "success" },
        });
      }
    } catch {
      toast.add({
        title: t("hub.catalog_manager.package_update_error", {
          defaultValue: "Could not update package",
        }),
        data: { type: "error" },
      });
    }
  }, [
    upgradeConfirmState,
    cartPackage,
    catalog,
    updateHubCatalog,
    updateHubPackage,
    t,
    toast,
  ]);

  const handleExplorerNewPackageCreate = useCallback(
    async ({
      label,
      description,
      visibility,
    }: {
      label: string;
      description: string;
      visibility: IHubPackageVisibility;
    }) => {
      if (catalog == null) {
        throw new Error("missing catalog");
      }
      const body: IHubPackage = {
        companyId,
        name: label,
        label,
        description,
        status: HubPackageStatus.Ready,
        catalogRelations: [
          {
            catalogId: catalog.catalogId ?? 0,
            catalogVersion: catalog.version ?? 0,
          },
        ],
        visibility,
      };

      try {
        const newPackage = await createHubPackage(body);
        if (cartPackage == null && newPackage.packageId != null) {
          try {
            await createHubCart({ packageId: newPackage.packageId });
          } catch (e) {
            console.error("Failed to create hub cart", e);
          }
        }
        toast.add({
          title: t("hub.catalog_manager.package_create_success", {
            defaultValue: "Package added",
          }),
          data: { type: "success" },
        });
      } catch {
        toast.add({
          title: t("hub.catalog_manager.package_create_error", {
            defaultValue: "Could not add package",
          }),
          data: { type: "error" },
        });
        throw new Error("createHubPackage failed");
      }
    },
    [
      catalog,
      createHubPackage,
      createHubCart,
      cartPackage,
      t,
      toast,
      companyId,
    ],
  );

  const footerActions = useMemo((): ReactNode => {
    const reviewStatus = catalog?.status;
    return (
      <div className="flex shrink-0 flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={goBackOrToCatalogManager}>
          {t("hub.administer.cancel", { defaultValue: "Cancel" })}
        </Button>
        {mode === CatalogMode.Details && isFromExplorerHubTab && (
          <WithPermission permissions={[HUB_PERM.CATALOG_BUY]}>
            <Button
              type="button"
              size="sm"
              variant={explorerCatalogInCart ? "outline" : undefined}
              disabled={isPackageBusy}
              onClick={
                explorerCatalogInCart
                  ? handleExplorerRemoveFromCartClick
                  : handleExplorerAddToCartClick
              }>
              {explorerCatalogInCart
                ? t("hub.catalog_manager.remove_from_cart_aria", {
                    defaultValue: "Remove from package",
                  })
                : t("hub.catalog_manager.package_dialog_add", {
                    defaultValue: "Add to package",
                  })}
            </Button>
          </WithPermission>
        )}
        {mode === CatalogMode.Details &&
          !isFromExplorerHubTab &&
          !isFromSubscriptionsHubTab &&
          !isFromDeploymentsHubTab && (
            <WithPermission permissions={[HUB_PERM.CATALOG_EDIT]}>
              <Button size="sm" asChild>
                <Link to={pathToEditCatalog(catalogId, effectiveVersion ?? "")}>
                  {t("hub.administer.edit", {
                    defaultValue: "Edit",
                  })}
                </Link>
              </Button>
            </WithPermission>
          )}
        {mode === CatalogMode.Review && catalog && reviewStatus != null && (
          <WithPermission permissions={[HUB_PERM.CATALOG_REVIEW_PARTICIPATE]}>
            {showModeratorReviewRejectStatus(reviewStatus) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isPostingPrbReview}
                onClick={() => setPendingModeratorAction({ approved: false })}>
                {t("hub.catalog_manager.reviews_reject", {
                  defaultValue: "Reject",
                })}
              </Button>
            )}
            {showModeratorReviewApproveStatus(reviewStatus) && (
              <Button
                type="button"
                size="sm"
                disabled={isPostingPrbReview}
                onClick={() => setPendingModeratorAction({ approved: true })}>
                {t("hub.catalog_manager.reviews_approve", {
                  defaultValue: "Approve",
                })}
              </Button>
            )}
          </WithPermission>
        )}
      </div>
    );
  }, [
    mode,
    goBackOrToCatalogManager,
    isFromExplorerHubTab,
    isFromSubscriptionsHubTab,
    isFromDeploymentsHubTab,
    catalogId,
    catalog,
    t,
    setPendingModeratorAction,
    isPostingPrbReview,
    explorerCatalogInCart,
    handleExplorerAddToCartClick,
    handleExplorerRemoveFromCartClick,
    isPackageBusy,
  ]);

  const catalogNotFound = (
    <ErrorFallback
      title={t("hub.catalog_details.catalog_load_error_title", {
        defaultValue: "Could not load catalog",
      })}
      message={t("hub.catalog_details.not_found", {
        defaultValue: "Catalog not found.",
      })}
      className="min-h-[240px]"
    />
  );

  let body: ReactNode = null;
  if (!idReady) {
    body = catalogNotFound;
  } else if (hasServerId && !isLoading && !catalog) {
    body = catalogNotFound;
  } else if (catalog) {
    const label = catalog.label || "-";
    const overview = catalog.overview || "";
    const description = catalog.description || "";
    const supportedCloud = catalog.supportedCloud ?? [];
    const mediaImages = catalog.media ?? [];
    const tabItems = [
      {
        id: "details",
        label: t("hub.catalog_details.tab_details", {
          defaultValue: "Details",
        }),
        children: <CatalogDescription description={description} />,
      },
      {
        id: "configuration",
        label: t("hub.catalog_details.tab_configuration", {
          defaultValue: "Configuration",
        }),
        children: (
          <CatalogTemplateStep
            forms={[
              ...(catalogType?.formId
                ? [
                    {
                      formId: catalogType.formId,
                      type: "catalogType" as const,
                      label: catalogTypeDisplayLabel,
                    },
                  ]
                : []),
              ...(catalog?.formId
                ? [
                    {
                      formId: catalog.formId,
                      type: "catalog" as const,
                      label: catalog.label,
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
      ...(catalog.addon
        ? []
        : [
            {
              id: "addons",
              label: t("hub.catalog_details.tab_supported_addons", {
                defaultValue: "Supported Addons",
              }),
              children: <CatalogAddons catalogId={catalog.catalogId} />,
            },
          ]),
      {
        id: "comments",
        label:
          mode === CatalogMode.Review
            ? t("hub.catalog_details.tab_review_comments", {
                defaultValue: "Review Comments",
              })
            : t("hub.catalog_details.tab_comments", {
                defaultValue: "Comments",
              }),
        children: (
          <CatalogComments
            catalogId={catalog.catalogId}
            catalogVersion={catalog.version}
            mode={mode}
          />
        ),
      },
    ];

    const showPageFooter =
      mode === CatalogMode.Details || mode === CatalogMode.Review;

    body = (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-col items-center px-6 pb-4 pt-4 shrink-0 z-10">
          <div className="mx-auto flex w-full max-w-3xl flex-col">
            <CatalogHeader
              label={label}
              catalogTypeLabel={catalogTypeDisplayLabel}
              status={catalog.status}
              visibility={catalog.visibility}
              supportedCloud={supportedCloud}
              mode={mode}
              catalogId={catalog.catalogId}
              icon={catalog.icon}
              averageRating={catalog.averageRating}
            />
          </div>
        </div>
        <BodyFooterLayout footer={showPageFooter ? footerActions : undefined}>
          <div className="mx-auto flex w-full max-w-3xl flex-col mt-4">
            <div className="flex w-full flex-col gap-4">
              <CatalogOverview
                overview={overview}
                typeId={
                  catalog.typeId != null ? String(catalog.typeId) : undefined
                }
              />
              <CatalogMetadata
                createdBy={catalog.createdBy}
                version={catalog.version}
                typeId={
                  catalog.typeId != null ? String(catalog.typeId) : undefined
                }
                supportedCloud={supportedCloud}
                tierId={String(catalog.tierId ?? "")}
                documentationLocations={catalog.documentationLocations ?? []}
              />
              <MediaGallery images={mediaImages} size="md" className="w-full" />
              <ScrollSyncTabs
                className="w-full"
                headerClassName="-mx-6 px-6"
                items={tabItems}
                aria-label={t("hub.catalog_details.tabs_label", {
                  defaultValue: "Catalog sections",
                })}
              />
            </div>
          </div>
        </BodyFooterLayout>
      </div>
    );
  }

  return (
    <WithPermission permissions={catalogDetailsTabRule}>
      {body}
      {moderatorConfirmDialog != null && (
        <ConfirmAction
          open={pendingModeratorAction != null}
          onOpenChange={(open: boolean) =>
            !open && setPendingModeratorAction(null)
          }
          title={moderatorConfirmDialog.title}
          description={moderatorConfirmDialog.description}
          confirmText={moderatorConfirmDialog.confirmText}
          cancelText={t("hub.administer.cancel", {
            defaultValue: "Cancel",
          })}
          actionVariant={moderatorConfirmDialog.variant}
          onConfirm={() => void handleModeratorConfirm()}
        />
      )}
      <ConfirmAction
        open={upgradeConfirmState != null}
        onOpenChange={(open) => !open && setUpgradeConfirmState(null)}
        title={
          upgradeConfirmState?.action ===
          CatalogVisibilityUpgradeAction.UpgradePlatformReview
            ? t("hub.checkout.upgrade_platform_confirm_title", {
                defaultValue:
                  "Update catalog visibility and submit for review?",
              })
            : t("hub.checkout.upgrade_confirm_title", {
                defaultValue: "Update catalog visibility?",
              })
        }
        description={
          upgradeConfirmState?.action ===
          CatalogVisibilityUpgradeAction.UpgradeCompany
            ? t("hub.checkout.upgrade_company_description", {
                defaultValue:
                  "To add this catalog to your package, its visibility will be changed to Company. Do you want to proceed?",
              })
            : t("hub.checkout.upgrade_platform_description", {
                defaultValue:
                  "To add this catalog to your package, its visibility will be changed to Platform and it will be submitted for review. Do you want to proceed?",
              })
        }
        confirmText={t("hub.checkout.upgrade", {
          defaultValue: "Update",
        })}
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant="default"
        onConfirm={handleUpgradeConfirm}
      />
    </WithPermission>
  );
}
